package top.wcpe.allincore.dsl.compiler.type

import top.wcpe.allincore.dsl.compiler.ast.TypeAst

/**
 * M5-c：将 [TypeAst]（解析期语法承载）映射到编译期类型系统 [IrType]。
 *
 * <p>解析策略：
 * <ul>
 *   <li>内置命名类型精确映射：Int/Long/Double/Number → NumberT，String → StringT，
 *       Bool/Boolean → BooleanT，Any → AnyT，Unit → UnitT。</li>
 *   <li>泛型容器递归解析实参：List&lt;T&gt; → ListT(T)，Map&lt;K,V&gt; → MapT(K,V)；
 *       未识别的元素类型宽容降级为 AnyT（避免整体返回 null 触发额外报错）。</li>
 *   <li>函数类型降级为 AnyT：运行期按 LambdaValue 处理，不做精确签名校验。</li>
 *   <li>未知命名类型（例如 {@code Color}）返回 null，由调用方决定是否报 E621。</li>
 *   <li>{@code nullable = true} 统一包装为 [IrType.Nullable]。</li>
 * </ul>
 * </p>
 *
 * <p>与 [top.wcpe.allincore.dsl.compiler.semantic.StateTypeChecker] 内部 toIrType 的差异：
 * 本处对未知命名类型返回 null（用于触发 E621），StateTypeChecker 则是降级为 AnyT（放行）。
 * 两处语义不同，不合并，避免影响 state 字段的既有行为。</p>
 */
object TypeResolver {

    /** 基础命名类型表。 */
    private val NAMED_BUILTINS: Map<String, IrType> = mapOf(
        "Int" to IrType.NumberT,
        "Long" to IrType.NumberT,
        "Double" to IrType.NumberT,
        "Float" to IrType.NumberT,
        "Number" to IrType.NumberT,
        "String" to IrType.StringT,
        "Bool" to IrType.BooleanT,
        "Boolean" to IrType.BooleanT,
        "Any" to IrType.AnyT,
        "Unit" to IrType.UnitT,
    )

    /**
     * 将 [ast] 解析为 [IrType]；未识别命名类型时返回 null。
     *
     * @return 对应的 IrType；命名类型不在内置表中时返回 null，调用方应据此上报 E621。
     */
    fun resolve(ast: TypeAst): IrType? = when (ast) {
        is TypeAst.Named -> resolveNamed(ast)
        is TypeAst.Generic -> resolveGeneric(ast)
        is TypeAst.Function -> resolveFunction(ast)
    }

    private fun resolveNamed(ast: TypeAst.Named): IrType? {
        val base = NAMED_BUILTINS[ast.name] ?: return null
        return if (ast.nullable) IrType.Nullable(base) else base
    }

    private fun resolveGeneric(ast: TypeAst.Generic): IrType? {
        // 第一版仅识别 List / Map 两类容器；其它泛型名退回 null，由调用方决定是否报错。
        val base: IrType = when (ast.name) {
            "List" -> {
                // 元素类型递归解析；未识别（resolve 返回 null）或 args 为空时降级为 AnyT。
                val arg = if (ast.args.isNotEmpty()) resolve(ast.args[0]) ?: IrType.AnyT else IrType.AnyT
                IrType.ListT(arg)
            }
            "Map" -> {
                // DSL 仅支持字符串 key（语法层保证）；这里仍按通用 resolve 走，未声明时降为 AnyT。
                val key = if (ast.args.size >= 1) resolve(ast.args[0]) ?: IrType.AnyT else IrType.AnyT
                val value = if (ast.args.size >= 2) resolve(ast.args[1]) ?: IrType.AnyT else IrType.AnyT
                IrType.MapT(key, value)
            }
            else -> return null
        }
        return if (ast.nullable) IrType.Nullable(base) else base
    }

    private fun resolveFunction(ast: TypeAst.Function): IrType {
        // 第一版函数类型降级为 AnyT；运行期按 LambdaValue 统一处理。
        val base: IrType = IrType.AnyT
        return if (ast.nullable) IrType.Nullable(base) else base
    }
}
